import { FilterSpecification, Layer, LayerProps } from 'shared/map-exports';
import { usePoisState } from 'state/usePoisState';
import { MapClickGetPois, MapLayerProps } from 'features/mapping/mapping.types';
import { FirePerimeterPoi } from 'features/mapping/components/dialogs/PoiDialog/PoiDialog.types';
import { getWatchdutyTileUrl } from 'features/mapping/components/layers/layers.utils';
import {
  getSelectedPatternStyle,
  getSelectedStrokeStyle,
} from 'features/mapping/mapping.utils';
import {
  BELOW_STREETS_LAYER_ID,
  ON_TOP_OF_EVERYTHING_LAYER_ID,
} from 'features/mapping/styles/constants';
import { VisibilityToggledSource } from '../VisibilityToggledSource';

// `review` renders at higher opacity and fully opaque strokes so approved
// perimeters read at the same visual weight as the editable pending layer.
type ActiveFirePerimetersLayerMode = 'default' | 'review';

type ActiveFirePerimetersLayerProps = MapLayerProps & {
  interactive?: boolean;
  excludedPerimeterId?: number;
  /**
   * Prefix for this instance's Mapbox layer ids. Needed when the layer is
   * mounted more than once on a map (e.g. the edit form's excluding instance),
   * since layer ids must be unique.
   */
  layerIdPrefix?: string;
  /** Visual style preset; use `review` in the edit/approval context. */
  mode?: ActiveFirePerimetersLayerMode;
};

type ActiveFirePerimetersProperties = {
  source: string;
  source_date_current: string;
  source_display_string: string;
};

const FIRIS_LAYER_REFRESH_SECONDS = 60;
const FIRIS_LAYER_OUTER_STROKE_WIDTH = 8;

export const FIRE_PERIMETERS_FILL_LAYER_ID = 'perimeters-fill';

// Filters used for applying different styles to different fire perimeter sources.
const firisLayerFilter: FilterSpecification = ['==', 'source', 'firis'];
// Matches every `source` except `firis` (NIFC, and other sources).
const otherPerimeterLayerFilter: FilterSpecification = [
  '!=',
  'source',
  'firis',
];

const activeFirePerimetersFillStyle: LayerProps = {
  type: 'fill',
  'source-layer': 'fireperimeters_active',
  paint: {
    'fill-color': '#FF0000',
    'fill-opacity': ['case', firisLayerFilter, 0.4, 0.25],
  },
};

const otherPerimetersStrokeStyle: LayerProps = {
  type: 'line',
  'source-layer': 'fireperimeters_active',
  filter: otherPerimeterLayerFilter,
  paint: {
    'line-color': '#FF0000',
    'line-opacity': 0.75,
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 18, 3.5],
  },
};

const firisPerimetersStrokeStyle: LayerProps = {
  type: 'line',
  'source-layer': 'fireperimeters_active',
  filter: firisLayerFilter,
  paint: {
    'line-color': '#FD2D2C',
    'line-opacity': 1,
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 18, 2.5],
    'line-dasharray': [2, 1],
  },
};

const firisPerimetersBorderStyle: LayerProps = {
  type: 'line',
  'source-layer': 'fireperimeters_active',
  filter: firisLayerFilter,
  layout: {
    'line-cap': 'square',
  },
  paint: {
    'line-color': '#FD2D2C',
    'line-opacity': 0.32,
    'line-width': [
      'interpolate',
      ['linear'],
      ['zoom'],
      12,
      FIRIS_LAYER_OUTER_STROKE_WIDTH / 12,
      18,
      FIRIS_LAYER_OUTER_STROKE_WIDTH,
    ],
    'line-offset': [
      'interpolate',
      ['linear'],
      ['zoom'],
      12,
      -FIRIS_LAYER_OUTER_STROKE_WIDTH / 24,
      18,
      -FIRIS_LAYER_OUTER_STROKE_WIDTH / 2,
    ],
  },
};

// Review-mode overrides: spread the base paint, change only what differs.
const reviewFillOverride: LayerProps = {
  type: 'fill',
  paint: { ...activeFirePerimetersFillStyle.paint, 'fill-opacity': 0.6 },
};

const reviewOtherStrokeOverride: LayerProps = {
  type: 'line',
  paint: { ...otherPerimetersStrokeStyle.paint, 'line-opacity': 1 },
};

const reviewFirisBorderOverride: LayerProps = {
  type: 'line',
  paint: { ...firisPerimetersBorderStyle.paint, 'line-opacity': 1 },
};

export const firePerimeterClickHandler: MapClickGetPois<
  ActiveFirePerimetersProperties
> = (geoJsonFeatures) => {
  return geoJsonFeatures.map((feature) => {
    const { properties } = feature;
    const metadata: FirePerimeterPoi = {
      id: feature.id,
      source: properties.source_display_string,
      dateCurrent: properties.source_date_current,
    };
    return {
      type: 'firePerimeter',
      id: feature.id,
      metadata,
    };
  });
};

export const ActiveFirePerimetersLayer = (
  props: ActiveFirePerimetersLayerProps,
): JSX.Element => {
  const {
    visible,
    interactive = true,
    excludedPerimeterId,
    layerIdPrefix = '',
    mode = 'default',
  } = props;
  const { selectedPoi } = usePoisState();

  const isReview = mode === 'review';

  const selectedId =
    (selectedPoi?.type === 'firePerimeter' && selectedPoi.id) || '';

  const selectedFillStyle = getSelectedPatternStyle({
    idKey: ['id'],
    selectedId,
    otherLayerProps: {
      'source-layer': 'fireperimeters_active',
    },
  });

  const selectedStrokeStyle = getSelectedStrokeStyle({
    idKey: ['id'],
    selectedId,
    otherLayerProps: {
      'source-layer': 'fireperimeters_active',
    },
  });

  const excludeFirePerimeterFilter: FilterSpecification =
    excludedPerimeterId === undefined
      ? ['all']
      : ['!=', ['id'], excludedPerimeterId];

  return (
    <VisibilityToggledSource
      url={getWatchdutyTileUrl('fireperimeters_active')}
      volatile
      visible={visible}
      refreshIntervalSeconds={FIRIS_LAYER_REFRESH_SECONDS}
    >
      <Layer
        id={`${layerIdPrefix}${FIRE_PERIMETERS_FILL_LAYER_ID}`}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...activeFirePerimetersFillStyle}
        {...(isReview ? reviewFillOverride : {})}
        filter={excludeFirePerimeterFilter}
        metadata={{ interactive }}
      />
      <Layer
        id={`${layerIdPrefix}other-perimeter-stroke`}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...otherPerimetersStrokeStyle}
        {...(isReview ? reviewOtherStrokeOverride : {})}
        filter={excludeFirePerimeterFilter}
        metadata={{ interactive }}
      />
      <Layer
        id={`${layerIdPrefix}firis-perimeter-stroke`}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...firisPerimetersStrokeStyle}
        filter={excludeFirePerimeterFilter}
        metadata={{ interactive }}
      />
      <Layer
        id={`${layerIdPrefix}firis-perimeter-border`}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...firisPerimetersBorderStyle}
        {...(isReview ? reviewFirisBorderOverride : {})}
        filter={excludeFirePerimeterFilter}
        metadata={{ interactive }}
      />
      <Layer
        id={`${layerIdPrefix}perimeter-fill-selected`}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...selectedFillStyle}
      />
      <Layer
        id={`${layerIdPrefix}perimeter-stroke-selected`}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...selectedStrokeStyle}
      />
    </VisibilityToggledSource>
  );
};
